<template>
  <order-form v-if="localOrder" ref="orderForm" :order="localOrder">
    <template v-slot:action-buttons>
      <qtm-btn :loading="loading" secondary @click="submitChanges()">
        Submit Changes
      </qtm-btn>
    </template>
  </order-form>
</template>

<script setup lang="ts">
import type { Order } from '@quotetome/materials-api'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import OrderForm from '@/components/orders/order-form.vue'

export interface Props {
  order: Order
}

const props = defineProps<Props>()

const { $api, $error, $toast } = useNuxtApp()
const adminStore = useAdminStore()
const authStore = useAuthStore()

const loading = ref(false)
const localOrder = ref<Order>()

const cloneOrder = () => {
  localOrder.value = cloneDeep(props.order)
}

const changes = (submitData: object) => {
  const originalData = props.order.getSubmitData()
  originalData.po_number_format = props.order.po_number_format
  return !isEqual(originalData, submitData) || props.order.owner?.id !== localOrder.value?.owner?.id
}

const orderFormRef = useTemplateRef('orderForm')
const submitChanges = async (externalCaller = false) => {
  if (!localOrder.value) {
    return
  }

  if (!orderFormRef.value?.isValid()) {
    return
  }

  const orderData = (localOrder.value as any).getSubmitData()
  orderData.po_number_format = localOrder.value.po_number_format

  if (!changes(orderData)) {
    if (!externalCaller) {
      $toast.warning('No changes to submit')
    }
    return
  }

  if (authStore.isStaff && localOrder.value.owner) {
    orderData.owner = localOrder.value.owner.id
  }

  loading.value = true
  try {
    const updatedOrder = await $api.v1.rfqs.update(orderData, { strict: true })
    adminStore.updateOrder(updatedOrder)
    $toast.success('Order updated')
  }
  catch (error) {
    if (externalCaller) {
      throw error
    }
    $error.report(error)
  }
  finally {
    loading.value = false
  }
}

const beforeTransition = () => {
  return submitChanges(true)
}

onMounted(cloneOrder)

defineExpose({ beforeTransition, refresh: cloneOrder })
</script>
